// ═══════════════════════════════════════════════════════════════════════════
// clasificadores/centralPromos.js — Clasificador puro de Central de Promociones
// (réplica del modelo, 12-jul-2026).
// ───────────────────────────────────────────────────────────────────────────
// EXTRAÍDO de CentralPromocionesDashboard.jsx para PARIDAD vista↔agente:
// el dashboard importa `calcularAccionPromo`/`construirFilaPromo`/
// `construirPerfilesPorFamilia`/`calcularEmpresaPromedios`/`filasPromoPropia`
// de acá (misma función, mismos números), y el agente corre
// `clasificarCentralPromos` sobre las MISMAS colecciones canónicas mapeadas
// al shape legacy (centralPromosCanonicoShape).
//
// El motor de decisión ya evalúa margen con las Reglas de Decisión
// (`central-promociones`) — acá NO se aplica el gate genérico de rentabilidad.
//
// ACCIÓN DOBLE ("Sumarse apagando publi") — decisión Mariano 12-jul-2026:
// NO se emite una tarea compuesta. Se emite SOLO la primera mitad
// (pausar_ad). Cuando la publi quede apagada, la próxima corrida evalúa el
// ítem sin ACOS → cae en la regla simple de "Sumarse" → sale la segunda
// tarea (activar_promo). El pipeline vive en la re-detección, no en la
// tarea. El estado intermedio (publi apagada, sin promo) es el seguro.
//
// 5 secciones accionables:
//   sumarse_promo            → activar_promo  (candidato, neto ≥ umbral)
//   promo_propia             → activar_promo  (sin promo ML, margen aguanta
//                              el descuento default — Reglas de Decisión)
//   sumarse_apagando_publi   → pausar_ad      (mitad 1 de la acción doble)
//   apagar_publi_promo       → pausar_ad      (participando, publi come margen)
//   bajarse_promo            → pausar_promo   (participando, no cierra)
// Declarados (sin tarea): mantener, no-sumarse, sin-stock, sin-datos, pausadas.
// ═══════════════════════════════════════════════════════════════════════════

import { evaluarElegibilidadPromo, scoreParaSort } from '@modules/mercadolibre/lib/evaluarElegibilidadPromo'
import { clasificarUrgencia } from '@core/lib/urgenciaStock'
import {
  calcularPromedioEmpresaMargen,
  calcularPromedioEmpresaAcos,
  obtenerBenchmarkMargen,
  obtenerBenchmarkAcos,
} from '@modules/mercadolibre/lib/benchmarks'
import { clasificarPerfilVenta, indexarLineasPorFamilia } from '@modules/mercadolibre/lib/perfilVentaHistorico'
import { getDefaultsParametros } from '@modules/plataforma/lib/reglasDecisionDefaults'
import { tsAMs } from '@core/lib/syncJobProgramado'

// ─── Promedios de la cuenta (empresa) ──────────────────────────────────────
// Espejo del memo `empresaPromedios` del dashboard. Fuente preferida:
// empresas/{id}._aprendizajes (Capa 4 persistida). Fallback on-the-fly sobre
// el array de publicaciones en memoria.
export function calcularEmpresaPromedios({ aprendizajes = null, items = [] } = {}) {
  if (aprendizajes && aprendizajes.estado === 'calculado') {
    const margenPct = typeof aprendizajes.margenSinPubPctProm === 'number' ? aprendizajes.margenSinPubPctProm : null
    const acosPct = typeof aprendizajes.acosProm === 'number' ? aprendizajes.acosProm : null
    const netoPct = typeof aprendizajes.netoPctProm === 'number'
      ? aprendizajes.netoPctProm
      : (margenPct != null && acosPct != null ? margenPct - acosPct : margenPct)
    return {
      margenPct,
      acosPct,
      netoPct,
      pubsMargen: aprendizajes.samples?.pubsConMargen || 0,
      pubsAcos: aprendizajes.samples?.pubsConAds || 0,
      fuente: 'persistido',
    }
  }
  if (!Array.isArray(items) || items.length === 0) return null
  const margen = calcularPromedioEmpresaMargen(items)
  const acos = calcularPromedioEmpresaAcos(items)
  const margenPct = typeof margen.valor === 'number' ? margen.valor : null
  const acosPct = typeof acos.valor === 'number' ? acos.valor : null
  const netoPct = (margenPct != null && acosPct != null)
    ? margenPct - acosPct
    : (margenPct != null ? margenPct : null)
  return {
    margenPct,
    acosPct,
    netoPct,
    pubsMargen: margen.publicaciones || 0,
    pubsAcos: acos.publicaciones || 0,
    fuente: 'on-the-fly',
  }
}

// ─── Perfil histórico de venta — agrupado por familyId ──────────────────────
// Espejo del memo `perfilesPorFamilia` del dashboard. Recorre las órdenes
// (ventana 90d) y clasifica cada familia según qué palancas (publi y/o
// descuento) usó históricamente para vender.
export function construirPerfilesPorFamilia({ ventas = [], pubsById = new Map(), reglas = {} } = {}) {
  if (!Array.isArray(ventas) || ventas.length === 0) return new Map()
  if (!pubsById || pubsById.size === 0) return new Map()
  const lineasPorFam = indexarLineasPorFamilia(ventas, pubsById)
  const out = new Map()
  const opciones = {
    minOrdenes: reglas?.perfilVentasMinOrdenes ?? 5,
    umbralAlto: reglas?.perfilVentasUmbralAlto ?? 0.5,
    umbralBajo: reglas?.perfilVentasUmbralBajo ?? 0.2,
  }
  for (const [famKey, lineas] of lineasPorFam.entries()) {
    let pubMuestra = null
    for (const it of pubsById.values()) {
      if ((it.familyId || it.itemId || it.id) === famKey) {
        pubMuestra = it
        break
      }
    }
    if (!pubMuestra) continue
    out.set(famKey, clasificarPerfilVenta({ lineas, pub: pubMuestra, opciones }))
  }
  return out
}

// ═══════════════════════════════════════════════════════════════════════════
// MOTOR DE DECISIÓN — 9 estados (refactor 2026-05-03; extraído 12-jul-2026)
// ───────────────────────────────────────────────────────────────────────────
// Filosofía: las reglas DECIDEN, no piden "evaluar" ni clasifican como
// "al límite/atento". Si la regla no alcanza para decidir, falta info, no
// matiz. El motor combina:
//
//   • Margen post-promo (sin publi y neto con publi)
//   • Stock vs reposición (urgenciaStock)
//   • Perfil histórico de venta (publi/descuento) ← lib perfilVentaHistorico
//   • ACOS item vs ACOS empresa (palanca redundante)
//
// Regla de oro: "apagás la palanca redundante o más cara".
//
// Estados:
//   🟢 Mantener / Sumarse                 → todo OK
//   🟢 Promo propia                       → sin promo elegible + margen sobrado + vende solo
//   🟡 Sumarse apagando publi             → publi cara/redundante, descuento OK
//   🟡 Apagar publi en promo              → ya estás adentro, publi se come margen
//   🔴 Bajarse                            → participando + bloqueante (margen O stock crítico)
//   🔴 No sumarse                         → candidato + bloqueante (margen, stock, perfil)
//   ⚫ Sin stock / Sin datos              → defensivos
//
// Devuelve { id, label, emoji, color, razon, _calc } — `_calc` expone los
// números del motor (margen post sin publi / neto, umbral, ACOS) para que el
// clasificador arme las métricas de la tarea sin recalcular.
// ═══════════════════════════════════════════════════════════════════════════
export function calcularAccionPromo(f, reglas, empresaPromedios) {
  const v = f.veredicto || {}
  const r = reglas || {}

  const descRatio = (f.sellerDisc$ > 0 && f.baseCalculo > 0)
    ? (f.sellerDisc$ / f.baseCalculo)
    : (f.sellerPct > 0 ? f.sellerPct / 100 : null)
  const margenPostSinPubli = (v.margenSinAdsActualPct != null && descRatio != null)
    ? v.margenSinAdsActualPct - descRatio
    : null
  const margenPostNeto = (margenPostSinPubli != null && v.acosActualPct != null)
    ? margenPostSinPubli - v.acosActualPct
    : margenPostSinPubli

  // Umbral OK — el más exigente entre piso absoluto y relativo a empresa.
  const pisoAbs = r.margenNetoPisoAbsoluto ?? 0.10
  const pisoRel = r.margenNetoPisoRelativo ?? 0.50
  const empresaNeto = empresaPromedios?.netoPct ?? null
  const empresaAcos = empresaPromedios?.acosPct ?? null
  const umbralOk = empresaNeto != null
    ? Math.max(pisoAbs, pisoRel * empresaNeto)
    : pisoAbs

  // Coef "ACOS caro vs empresa" — multiplicador por encima del cual la publi
  // del item se considera redundante/cara dado el contexto de la promo.
  const coefAcosCaro = r.coefAcosCaroVsEmpresa ?? 1.5
  const acosItem = v.acosActualPct ?? null
  const publiCara = (acosItem != null && empresaAcos != null && empresaAcos > 0)
    ? acosItem > empresaAcos * coefAcosCaro
    : false

  const esCandidato = f.status === 'candidate' || f.status === 'pending'
  const esParticipando = f.status === 'started' || f.status === 'programmed'
  const stockQuebrado = v.urgenciaStock === 'quebrado'
  const stockCritico = v.urgenciaStock === 'critico'

  // Perfil histórico (de lib/perfilVentaHistorico). Puede ser null si no se
  // calculó o 'pocos-datos' si no hay órdenes suficientes.
  const perfilId = f.perfilVenta?.perfil?.id || null

  const fmt = (x) => x == null ? '—' : (x * 100).toFixed(1) + '%'

  // Números del motor — se adjuntan como `_calc` a la acción decidida.
  const _calc = {
    descRatio,
    margenPostSinPubli,
    margenPostNeto,
    umbralOk,
    acosItem,
    empresaAcos,
    empresaNeto,
    publiCara,
    perfilId,
  }

  const accion = (() => {
    // ─── 1. Sin datos ──────────────────────────────────────────────────────
    if (margenPostSinPubli == null) {
      return { id: 'sin-datos', label: 'Sin datos', emoji: '❓', color: 'text-zinc-500 bg-zinc-500/10 border-zinc-500/20', razon: 'Faltan datos de margen o costo de la promo.' }
    }

    // ─── 2. Sin stock (defensivo: ML suele pausar) ─────────────────────────
    if (stockQuebrado && (esParticipando || esCandidato)) {
      return { id: 'sin-stock', label: 'Sin stock', emoji: '⚫', color: 'text-zinc-500 bg-zinc-500/10 border-zinc-500/20', razon: 'Stock QUEBRADO. ML normalmente pausa la publicación automáticamente — verificar.' }
    }

    // ─── 3. Stock crítico — bajarse / no sumarse ───────────────────────────
    // La acción de "reponer" vive en el módulo de Velocidad de Consumo, ese es
    // el dueño de la decisión de stock. Acá solo decidimos sobre la promo: si
    // el stock no alcanza, la única acción accionable es bajarse o no sumarse.
    // Unificado bajo "Bajarse" para no fragmentar el filtro (mismo accionable).
    if (stockCritico && esParticipando) {
      return { id: 'bajarse', label: 'Bajarse', emoji: '🔴', color: 'text-red-300 bg-red-500/15 border-red-500/30', razon: 'Stock CRÍTICO durante la promo. Bajate para no quemar el saldo. (Reponer se gestiona desde Velocidad de consumo.)' }
    }
    if (stockCritico && esCandidato) {
      return { id: 'no-sumarse', label: 'No sumarse', emoji: '🔴', color: 'text-red-300 bg-red-500/15 border-red-500/30', razon: 'Stock CRÍTICO. No alcanza para la promo. (Reponer se gestiona desde Velocidad de consumo.)' }
    }

    // ─── 4. Bloqueante de margen (rojo: ni apagando publi cierra) ──────────
    // Si margen sin publi ya es negativo, no hay forma de salvarlo apagando publi.
    if (margenPostSinPubli < umbralOk * 0) {
      // umbralOk * 0 = 0; estricto: margen < 0 = pérdida garantizada
      if (esCandidato) {
        return { id: 'no-sumarse', label: 'No sumarse', emoji: '🔴', color: 'text-red-300 bg-red-500/15 border-red-500/30', razon: `Margen post-promo (sin publi) ${fmt(margenPostSinPubli)}. La promo da pérdida aunque apagues publi.` }
      }
      if (esParticipando) {
        return { id: 'bajarse', label: 'Bajarse', emoji: '🔴', color: 'text-red-300 bg-red-500/15 border-red-500/30', razon: `Margen post-promo (sin publi) ${fmt(margenPostSinPubli)}. La promo da pérdida aunque apagues publi.` }
      }
    }

    // ─── 5. Apagar publi en promo (participando + publi se come margen) ────
    // Margen sin publi es positivo, pero con publi queda en pérdida o por
    // debajo del piso → conviene apagar publi durante la promo.
    if (esParticipando && margenPostNeto != null && (margenPostNeto < 0 || margenPostNeto < umbralOk)) {
      if (margenPostSinPubli >= umbralOk) {
        return { id: 'apagar-publi-en-promo', label: 'Apagar publi en promo', emoji: '🟡', color: 'text-amber-200 bg-amber-500/15 border-amber-500/30', razon: `Sin publi: ${fmt(margenPostSinPubli)} (cierra). Con publi: ${fmt(margenPostNeto)} (no cierra). Apagá publi durante la promo.` }
      }
    }

    // ─── 6. Sumarse apagando publi (candidato + ACOS caro o perfil "solo desc") ─
    // Caso A: ACOS del item es muy caro vs empresa → la publi sería redundante
    //         con la promo (la promo tracciona; pagar ads encima es regalar margen).
    // Caso B: histórico muestra que el item vende solo con descuento (sin publi).
    if (esCandidato && (publiCara || perfilId === 'solo-desc')) {
      if (margenPostSinPubli >= umbralOk) {
        const motivo = publiCara
          ? `ACOS item ${fmt(acosItem)} > ${coefAcosCaro.toFixed(1)}× empresa (${fmt(empresaAcos)}). Apagá publi al sumarte.`
          : `Histórico: vende con descuento, sin publi. Apagá publi al sumarte.`
        return { id: 'sumarse-apagando-publi', label: 'Sumarse apagando publi', emoji: '🟡', color: 'text-amber-200 bg-amber-500/15 border-amber-500/30', razon: motivo }
      }
    }

    // ─── 7. No sumarse — perfil "solo publi" (descuento es regalo) ─────────
    // Si el item históricamente se vende con publi sin necesitar descuento,
    // sumarse a la promo regala margen. Mantener publi prendida, no entrar.
    if (esCandidato && perfilId === 'solo-publi') {
      return { id: 'no-sumarse', label: 'No sumarse', emoji: '🔴', color: 'text-red-300 bg-red-500/15 border-red-500/30', razon: 'Histórico: vende con publi sin necesitar descuento. La promo regala margen.' }
    }

    // ─── 8. Promo propia (sin promo elegible + margen sobrado + vende solo) ─
    // Cuando el item ni siquiera está como candidato y el perfil indica que
    // vende sin palancas, queda la opción de armar promo propia agresiva.
    // El umbral usa `coefTop` de Reglas — definimos "margen sobrado" como
    // umbralOk × coefTop (= 1.2× por default).
    const coefTop = r.coefTop ?? 1.2
    if (!esCandidato && !esParticipando && perfilId === 'vende-solo') {
      if (margenPostSinPubli >= umbralOk * coefTop) {
        return { id: 'promo-propia', label: 'Promo propia', emoji: '🟢', color: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30', razon: `Vende solo (sin publi ni descuento) + margen sobrado (>= ${(coefTop).toFixed(1)}× umbral). Considerá promo propia agresiva.` }
      }
    }

    // ─── 9. OK — Mantener / Sumarse ────────────────────────────────────────
    if (margenPostNeto != null && margenPostNeto >= umbralOk) {
      if (esCandidato) {
        return { id: 'sumarse', label: 'Sumarse', emoji: '🟢', color: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30', razon: `Margen post-promo neto ${fmt(margenPostNeto)} (umbral ${fmt(umbralOk)}). Conviene entrar.` }
      }
      if (esParticipando) {
        return { id: 'mantener', label: 'Mantener', emoji: '🟢', color: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30', razon: `Margen post-promo neto ${fmt(margenPostNeto)} (umbral ${fmt(umbralOk)}). Stock OK.` }
      }
    }

    // Margen positivo pero por debajo del umbral OK — no llega a "OK puro".
    // Si no caímos en ningún caso anterior, lo mandamos a Bajarse/No sumarse
    // por consistencia: el motor decide, no propone "al límite".
    if (margenPostNeto != null && margenPostNeto < umbralOk) {
      if (esCandidato) {
        return { id: 'no-sumarse', label: 'No sumarse', emoji: '🔴', color: 'text-red-300 bg-red-500/15 border-red-500/30', razon: `Margen post-promo neto ${fmt(margenPostNeto)} < umbral ${fmt(umbralOk)}. No alcanza el piso.` }
      }
      if (esParticipando) {
        return { id: 'bajarse', label: 'Bajarse', emoji: '🔴', color: 'text-red-300 bg-red-500/15 border-red-500/30', razon: `Margen post-promo neto ${fmt(margenPostNeto)} < umbral ${fmt(umbralOk)}. No alcanza el piso.` }
      }
    }

    return { id: 'sin-datos', label: 'Sin datos', emoji: '❓', color: 'text-zinc-500 bg-zinc-500/10 border-zinc-500/20', razon: `Status: ${f.status || 'desconocido'}.` }
  })()

  return { ...accion, _calc }
}

// ─── Construir 1 fila evaluada (item × campaña) ─────────────────────────────
// Espejo PURO del useCallback `construirFilaPromo` del dashboard — recibe los
// mapas por parámetro en lugar de closures de React.
export function construirFilaPromo({
  itemId,
  promoEnItem,
  fuente,
  campania,
  itemById,
  productoPorItem,
  velocidadPorItem,
  publicacionesEmpresa,
  eventosCalendario = [],
  perfilesPorFamilia = new Map(),
} = {}) {
  const itemDoc = itemById.get(itemId)
  const productoDoc = productoPorItem.get(itemId)
  const velocidad = velocidadPorItem.get(itemId) ?? null

  const veredicto = evaluarElegibilidadPromo({
    item: itemDoc,
    producto: productoDoc,
    promoEnItem,
    campania,
    eventosCalendario,
    publicacionesEmpresa,
    velocidadDiaria: velocidad,
    tiempoReposicionDias: productoDoc?.tiempoReposicionDias,
  })

  // Perfil histórico de venta — clasificación por familia (cae a itemId
  // si no hay familia). Lo consume `calcularAccionPromo` para decidir
  // cuándo apagar publi (palanca redundante) o no entrar a la promo.
  const famKey = itemDoc?.familyId || itemId
  const perfilVenta = perfilesPorFamilia.get(famKey) || null

  return {
    itemId,
    perfilVenta,
    title: itemDoc?.title || '—',
    permalink: itemDoc?.permalink || null,
    thumbnail: itemDoc?.thumbnail || itemDoc?.secureThumbnail || null,
    productoId: productoDoc?.id || null,
    productoNombre: productoDoc?.nombre || null,
    // ─── Precio y status REALES de la publicación (no de la promo) ───
    precioPubActual: itemDoc?.price != null ? Number(itemDoc.price) : null,
    precioPubOriginal: itemDoc?.originalPrice != null ? Number(itemDoc.originalPrice) : (itemDoc?.original_price != null ? Number(itemDoc.original_price) : null),
    statusPub: itemDoc?.status || null,
    availableQuantityPub: itemDoc?.availableQuantity ?? itemDoc?.available_quantity ?? null,
    // Datos crudos de la promo
    price: Number(promoEnItem?.price || 0),
    originalPrice: Number(promoEnItem?.original_price || 0),
    meliPct: Number(promoEnItem?.meli_percentage || 0),
    sellerPct: Number(promoEnItem?.seller_percentage || 0),
    minDisc: promoEnItem?.min_discounted_price != null ? Number(promoEnItem.min_discounted_price) : null,
    maxDisc: promoEnItem?.max_discounted_price != null ? Number(promoEnItem.max_discounted_price) : null,
    sugDisc: promoEnItem?.suggested_discounted_price != null ? Number(promoEnItem.suggested_discounted_price) : null,
    topDeal: promoEnItem?.top_deal_price != null ? Number(promoEnItem.top_deal_price) : null,
    status: promoEnItem?.status,
    startDate: promoEnItem?.start_date,
    finishDate: promoEnItem?.finish_date,
    fuente,
    // ─── Cadena de cálculo del precio promocional ────────────────────
    // (mismos criterios y misma prioridad de fuentes que el panel de ML —
    //  ver comentario extenso en el historial del dashboard)
    ...((() => {
      const base = Number(promoEnItem?.original_price || 0)
        || (itemDoc?.price != null ? Number(itemDoc.price) : 0)
      const sPct = Number(promoEnItem?.seller_percentage || 0) / 100
      const mPct = Number(promoEnItem?.meli_percentage || 0) / 100
      let sellerDisc$ = base > 0 && sPct > 0 ? base * sPct : 0
      let meliDisc$   = base > 0 && mPct > 0 ? base * mPct : 0

      const precioFinalExplicit = Number(promoEnItem?.price || 0)
      const sugDisc = promoEnItem?.suggested_discounted_price != null ? Number(promoEnItem.suggested_discounted_price) : null
      const minDisc = promoEnItem?.min_discounted_price != null ? Number(promoEnItem.min_discounted_price) : null
      const maxDisc = promoEnItem?.max_discounted_price != null ? Number(promoEnItem.max_discounted_price) : null

      let precioComprador$
      let fuenteCalculo

      if (sellerDisc$ > 0 || meliDisc$ > 0) {
        // CO-FONDEADA / DEAL con split conocido
        precioComprador$ = (precioFinalExplicit > 0 && precioFinalExplicit < base)
          ? precioFinalExplicit
          : (base > 0 ? base - sellerDisc$ - meliDisc$ : 0)
        fuenteCalculo = 'split-cofond'
      } else if (precioFinalExplicit > 0 && precioFinalExplicit < base) {
        // ML mandó precio menor que el original — ya hay descuento aplicado.
        precioComprador$ = precioFinalExplicit
        sellerDisc$ = base - precioFinalExplicit
        meliDisc$ = 0
        fuenteCalculo = 'price-explicit'
      } else if (maxDisc != null && maxDisc > 0 && maxDisc < base) {
        // TRADICIONAL candidata — usar maxDisc (descuento MÍNIMO para entrar).
        precioComprador$ = maxDisc
        sellerDisc$ = base - maxDisc
        meliDisc$ = 0
        fuenteCalculo = 'rango-max'
      } else if (sugDisc != null && sugDisc > 0 && sugDisc < base) {
        // Fallback — sugerido por ML (suele ser MÁS agresivo que el mínimo).
        precioComprador$ = sugDisc
        sellerDisc$ = base - sugDisc
        meliDisc$ = 0
        fuenteCalculo = 'sugerido'
      } else {
        // Sin descuento aplicable — precio comprador queda en base.
        precioComprador$ = base
        fuenteCalculo = 'sin-descuento'
      }
      // Lo que ve el vendedor: solo se le descuenta su parte (ML aporta lo suyo).
      const recibeVendedor$ = base > 0 ? base - sellerDisc$ : 0
      return {
        baseCalculo: base,
        sellerDisc$,
        meliDisc$,
        precioComprador$,
        recibeVendedor$,
        fuenteCalculoPrecio: fuenteCalculo,
        precioFinalCalc: precioComprador$,
      }
    })()),
    // Veredicto completo del evaluador
    veredicto,
    sortScore: scoreParaSort(veredicto),
    campaniaId: campania?.id || null,
    campaniaNombre: campania?.name || campania?.id || null,
  }
}

// ─── Filas sintéticas "Promo propia" (fuente 3) ─────────────────────────────
// Espejo del bloque fuente-3 de `detalleAcciones`: pubs activas SIN promos ML
// cuyo margen aguanta el descuento default (Reglas de Decisión) → candidatas
// a promo propia. Opción B (decisión previa): cualquier pub con margen +
// stock que aguante el descuento.
export function filasPromoPropia({
  items = [],
  itemsConPromos = new Set(),
  productoPorItem = new Map(),
  velocidadPorItem = new Map(),
  perfilesPorFamilia = new Map(),
  reglas = {},
  empresaPromedios = null,
  conexionFiltro = null,
} = {}) {
  const filas = []
  const descuentoDefault = reglas?.descuentoPromoPropiaDefault ?? 0.15
  const coefPromoPropia = reglas?.coefPromoPropia ?? 1.0
  const pisoAbs = reglas?.margenNetoPisoAbsoluto ?? 0.10
  const pisoRel = reglas?.margenNetoPisoRelativo ?? 0.50
  const empresaNeto = empresaPromedios?.netoPct ?? null
  const umbralOk = empresaNeto != null
    ? Math.max(pisoAbs, pisoRel * empresaNeto)
    : pisoAbs
  const umbralPromoPropia = umbralOk * coefPromoPropia

  for (const it of (items || [])) {
    if (conexionFiltro && it.conexionId !== conexionFiltro) continue
    const itemId = it.itemId || it.id
    if (!itemId) continue
    if (itemsConPromos.has(itemId)) continue
    // Solo pubs activas — pausadas/cerradas no son candidatas operativas.
    if (it.status && it.status !== 'active') continue

    const productoDoc = productoPorItem.get(itemId)
    const velocidad = velocidadPorItem.get(itemId) ?? null
    const stock = Number(it.availableQuantity ?? it.available_quantity ?? 0)
    if (stock <= 0) continue  // sin stock no podemos hacer promo

    const benchMargen = obtenerBenchmarkMargen({
      itemDoc: it, productoDoc, publicacionesEmpresa: items,
    })
    const benchAcos = obtenerBenchmarkAcos({
      itemDoc: it, productoDoc, publicacionesEmpresa: items,
    })
    const margenSinAds = benchMargen?.valor ?? null
    const acos = benchAcos?.valor ?? 0
    if (margenSinAds == null) continue

    // Margen post-descuento neto: si después del descuento default y el
    // ACOS actual queda >= umbralPromoPropia → vale la pena considerar.
    const margenPostNetoSimulado = margenSinAds - descuentoDefault - acos
    if (margenPostNetoSimulado < umbralPromoPropia) continue

    // Urgencia stock — defensivo (no metemos en promo si está crítico).
    const diasHastaQuiebre = (velocidad && velocidad > 0) ? stock / velocidad : null
    const urgenciaStock = clasificarUrgencia(stock, diasHastaQuiebre, {
      modo: productoDoc?.tiempoReposicionDias ? 'reposicion' : 'generico',
      tiempoReposicion: productoDoc?.tiempoReposicionDias,
    })
    if (urgenciaStock === 'critico' || urgenciaStock === 'quebrado') continue

    const famKey = it.familyId || itemId
    const perfilVenta = perfilesPorFamilia.get(famKey) || null

    filas.push({
      itemId,
      title: it.title || '—',
      permalink: it.permalink || null,
      thumbnail: it.thumbnail || it.secureThumbnail || null,
      productoId: productoDoc?.id || null,
      productoNombre: productoDoc?.nombre || null,
      precioPubActual: it.price != null ? Number(it.price) : null,
      precioPubOriginal: it.originalPrice != null ? Number(it.originalPrice) : null,
      statusPub: it.status || null,
      availableQuantityPub: stock,
      // Datos sintéticos de promo (descuento default)
      price: 0,
      originalPrice: it.price != null ? Number(it.price) : 0,
      meliPct: 0,
      sellerPct: descuentoDefault * 100,
      baseCalculo: it.price != null ? Number(it.price) : 0,
      sellerDisc$: it.price != null ? Number(it.price) * descuentoDefault : 0,
      meliDisc$: 0,
      precioComprador$: it.price != null ? Number(it.price) * (1 - descuentoDefault) : 0,
      recibeVendedor$: it.price != null ? Number(it.price) * (1 - descuentoDefault) : 0,
      fuenteCalculoPrecio: 'promo-propia-sim',
      precioFinalCalc: it.price != null ? Number(it.price) * (1 - descuentoDefault) : 0,
      status: 'sin-promo-ml',  // status sintético — no es 'started' ni 'candidate'
      startDate: null,
      finishDate: null,
      fuente: 'sin-promo-ml',
      veredicto: {
        margenSinAdsActualPct: margenSinAds,
        acosActualPct: acos,
        margenSinAdsPostPct: margenSinAds - descuentoDefault,
        urgenciaStock,
        benchmarkMargen: benchMargen,
        benchmarkAcos: benchAcos,
      },
      sortScore: 0,
      campaniaId: '__promo_propia__',
      campaniaNombre: 'Sin promo ML (candidata a promo propia)',
      perfilVenta,
      // Pre-asignar acción directamente, sin pasar por calcularAccionPromo
      // (que requiere status candidate/started). Opción B: siempre que el
      // margen aguante el descuento, ofrecer la opción.
      accion: {
        id: 'promo-propia',
        label: 'Promo propia',
        emoji: '🟢',
        color: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30',
        razon: `Sin promo ML. Margen ${(margenSinAds * 100).toFixed(1)}% − descuento ${(descuentoDefault * 100).toFixed(0)}% − ACOS ${(acos * 100).toFixed(1)}% = ${(margenPostNetoSimulado * 100).toFixed(1)}% (umbral ${(umbralPromoPropia * 100).toFixed(1)}%). Bajá el precio para moverla.`,
        _calc: {
          margenPostSinPubli: margenSinAds - descuentoDefault,
          margenPostNeto: margenPostNetoSimulado,
          umbralOk: umbralPromoPropia,
          descRatio: descuentoDefault,
          acosItem: acos,
        },
      },
    })
  }
  return filas
}

const DIA_MS = 86_400_000

/**
 * Unidades por día de un item, desde el rollup de rentabilidad
 * (`_rentabilidadInferida`, `functions/lib/rentabilidadRollups.js`). El rollup
 * NO siempre cuenta 30 días: si en la ventana hay menos ventas que el mínimo,
 * toma las últimas N sin límite de fecha, y lo deja escrito en
 * `ventanaCriterio` (`'30d'` o `'last5'`). Dividir siempre por 30 inflaba la
 * velocidad de los items de baja rotación (report bv7lzeuE0DPnNgd1FVrP).
 *
 * El divisor de `lastN` se ancla en `actualizadoEn`, no en "ahora": la rama
 * `Nd` mide hasta el momento del cálculo, y con un rollup viejo las dos ramas
 * tienen que medir lo mismo. Sin el dato para saber la ventana → `null` (un
 * numerador derivado lleva su descriptor o no se divide, `agentes.js`).
 * @param {object|null|undefined} rent — `_rentabilidadInferida`
 * @returns {number|null}
 */
export function velocidadDiariaInferida(rent) {
  const u = rent?.unidadesVendidas
  if (typeof u !== 'number' || !(u > 0)) return null
  const criterio = String(rent.ventanaCriterio ?? '')
  const dias = /^\d+d$/.test(criterio) ? Number.parseInt(criterio, 10) : null
  if (dias) return u / dias
  if (!/^last\d+$/.test(criterio)) return null
  const desde = tsAMs(rent.ventanaDesde)
  const calculo = tsAMs(rent.actualizadoEn)
  if (desde == null || calculo == null) return null
  const diasVentana = (calculo - desde) / DIA_MS
  return diasVentana > 0 ? u / diasVentana : null
}

// ─── Índice de campañas para las filas ──────────────────────────────────────
// Espejo del DOMINIO de buildVistaCampanias (qué campañas existen y sus
// datos maestros), sin la agregación de KPIs que las filas no consumen.
// Los campos son exactamente los que usan evaluarElegibilidadPromo
// (name/type/start_date/finish_date) y construirFilaPromo (id/name).
export function construirCampaniasIndex({ campaniasMaster = [], promosPorItem = [], conexionFiltro = null } = {}) {
  const map = new Map()
  // 1) Master mlPromos — respeta el sweep de visibilidad (_visible === false
  //    se saltea, igual que buildVistaCampanias).
  for (const c of (campaniasMaster || [])) {
    if (conexionFiltro && c.conexionId !== conexionFiltro) continue
    if (c._visible === false) continue
    const id = c.promotionId || c.id
    if (!id) continue
    if (!map.has(id)) {
      map.set(id, {
        id,
        name: c.name || null,
        type: c.type || null,
        sub_type: c.sub_type || null,
        status: c.status || null,
        start_date: c.start_date || null,
        finish_date: c.finish_date || null,
        deadline_date: c.deadline_date || null,
        benefits: c.benefits || null,
      })
    }
  }
  // 2) Sembrar desde pub-primary campañas que el master no listó (mismo
  //    comportamiento que buildVistaCampanias origen 'pub-primary').
  for (const doc of (promosPorItem || [])) {
    if (conexionFiltro && doc.conexionId !== conexionFiltro) continue
    const promos = Array.isArray(doc.promociones) ? doc.promociones : []
    for (const p of promos) {
      if (!p?.id) continue
      if (!map.has(p.id)) {
        map.set(p.id, {
          id: p.id,
          name: p.name || null,
          type: p.type || null,
          sub_type: p.sub_type || null,
          status: p.status || null,
          start_date: p.start_date || null,
          finish_date: p.finish_date || null,
          deadline_date: p.deadline_date || null,
          benefits: null,
        })
      } else {
        const c = map.get(p.id)
        if (!c.name && p.name) c.name = p.name
        if (!c.type && p.type) c.type = p.type
        if (!c.start_date && p.start_date) c.start_date = p.start_date
        if (!c.finish_date && p.finish_date) c.finish_date = p.finish_date
      }
    }
  }
  return map
}

// ─── Construir TODAS las filas (espejo de detalleAcciones) ──────────────────
export function construirFilasCentralPromos({
  campaniasMaster = [],
  itemsPorCampania = [],   // canónica: [] (single-source pub-side) — se respeta el orden de fuentes
  promosPorItem = [],
  items = [],              // publicaciones shape central (familyId + _rentabilidadInferida)
  productos = [],
  catalogoLinks = [],
  ventas = [],             // shape cascada, ventana 90d (perfil de venta)
  eventosCalendario = [],
  reglas = {},
  aprendizajes = null,
  conexionFiltro = null,
} = {}) {
  // Mapas auxiliares (espejo de los memos del dashboard)
  const itemById = new Map()
  for (const it of (items || [])) {
    const id = it.itemId || it.id
    if (id) itemById.set(id, it)
  }

  const prodById = new Map()
  for (const p of (productos || [])) prodById.set(p.id, p)
  const productoPorItem = new Map()
  for (const link of (catalogoLinks || [])) {
    if (link.plataformaId && link.plataformaId !== 'mercado-libre') continue
    const prod = prodById.get(link.productoId)
    if (!prod) continue
    productoPorItem.set(link.externalItemId, prod)
  }

  const velocidadPorItem = new Map()
  for (const it of (items || [])) {
    const v = velocidadDiariaInferida(it?._rentabilidadInferida)
    if (v != null) velocidadPorItem.set(it.itemId || it.id, v)
  }

  const perfilesPorFamilia = construirPerfilesPorFamilia({ ventas, pubsById: itemById, reglas })
  const empresaPromedios = calcularEmpresaPromedios({ aprendizajes, items })
  const campaniaById = construirCampaniasIndex({ campaniasMaster, promosPorItem, conexionFiltro })

  const filas = []
  const itemIdsPorCampania = new Map()
  const itemsConPromos = new Set()
  const getSet = (campaniaId) => {
    if (!itemIdsPorCampania.has(campaniaId)) itemIdsPorCampania.set(campaniaId, new Set())
    return itemIdsPorCampania.get(campaniaId)
  }

  const ctxFila = { itemById, productoPorItem, velocidadPorItem, publicacionesEmpresa: items, eventosCalendario, perfilesPorFamilia }

  // Fuente 1: mlPromosItems (oficial — canónica: vacío)
  for (const it of (itemsPorCampania || [])) {
    if (conexionFiltro && it.conexionId !== conexionFiltro) continue
    const camp = campaniaById.get(it.promotionId)
    if (!camp) continue
    const visto = getSet(camp.id)
    if (visto.has(it.itemId)) continue
    filas.push(construirFilaPromo({ itemId: it.itemId, promoEnItem: it, fuente: 'oficial', campania: camp, ...ctxFila }))
    visto.add(it.itemId)
    itemsConPromos.add(it.itemId)
  }

  // Fuente 2: mlPublicacionesPromos (per-item, primaria en la canónica)
  for (const doc of (promosPorItem || [])) {
    if (conexionFiltro && doc.conexionId !== conexionFiltro) continue
    const itemId = doc.itemId || doc.id
    const promos = Array.isArray(doc.promociones) ? doc.promociones : []
    for (const promo of promos) {
      if (!promo?.id) continue
      const camp = campaniaById.get(promo.id)
      if (!camp) continue
      const visto = getSet(camp.id)
      if (visto.has(itemId)) continue
      filas.push(construirFilaPromo({ itemId, promoEnItem: promo, fuente: 'item', campania: camp, ...ctxFila }))
      visto.add(itemId)
      itemsConPromos.add(itemId)
    }
  }

  // Fuente 3: pubs huérfanas → candidatas a Promo propia
  filas.push(...filasPromoPropia({
    items, itemsConPromos, productoPorItem, velocidadPorItem,
    perfilesPorFamilia, reglas, empresaPromedios, conexionFiltro,
  }))

  // Acción por fila (las sintéticas ya vienen pre-asignadas)
  for (const f of filas) {
    if (f.accion) continue
    f.accion = calcularAccionPromo(f, reglas, empresaPromedios)
  }

  return { filas, empresaPromedios, campaniasEvaluadas: campaniaById.size }
}

// ─── Clasificador para el agente / vista 🎯 ─────────────────────────────────
// Secciones (claves GLOBALES de SECCION_DEFAULTS — no colisionar):
export const SECCIONES_CENTRAL_PROMOS = [
  'sumarse_promo',
  'promo_propia',
  'sumarse_apagando_publi',
  'apagar_publi_promo',
  'bajarse_promo',
]

const pct = (x) => x == null ? '—' : `${(x * 100).toFixed(1)}%`
const money = (v) => (v != null && isFinite(v)) ? `$${Math.round(Number(v)).toLocaleString('es-AR')}` : '—'

// accion.id del motor → sección de tareas
const ACCION_A_SECCION = {
  'sumarse': 'sumarse_promo',
  'promo-propia': 'promo_propia',
  'sumarse-apagando-publi': 'sumarse_apagando_publi',
  'apagar-publi-en-promo': 'apagar_publi_promo',
  'bajarse': 'bajarse_promo',
}

function itemDesdeFila(f, seccion) {
  const c = f.accion?._calc || {}
  const descPct = (f.baseCalculo > 0 && f.sellerDisc$ > 0)
    ? f.sellerDisc$ / f.baseCalculo
    : (f.sellerPct > 0 ? f.sellerPct / 100 : null)

  let motivo = `${f.accion?.emoji || ''} ${f.accion?.razon || ''}`.trim()
  if (f.campaniaNombre && f.campaniaId !== '__promo_propia__') {
    motivo = `${motivo} · Promo: ${f.campaniaNombre}`
  }
  if (seccion === 'sumarse_apagando_publi') {
    motivo = `${motivo} · Al apagar la publi, la próxima corrida va a sugerir sumarse (mitad 2).`
  }

  return {
    _itemId: String(f.itemId),
    title: f.title,
    _titulo: f.title,
    permalink: f.permalink,
    ...(f.productoId ? { _productoId: f.productoId } : {}),
    _motivo: motivo,
    _accionId: f.accion?.id || null,
    _margenPostNetoPct: c.margenPostNeto ?? null,
    _margenPostSinPubliPct: c.margenPostSinPubli ?? null,
    metricas: {
      campaniaId: f.campaniaId,
      campaniaNombre: f.campaniaNombre,
      statusPromo: f.status || null,
      razon: f.accion?.razon || null,
      margenPostSinPubliPct: c.margenPostSinPubli ?? null,
      margenPostNetoPct: c.margenPostNeto ?? null,
      umbralOkPct: c.umbralOk ?? null,
      acosItemPct: c.acosItem ?? null,
      acosEmpresaPct: c.empresaAcos ?? null,
      perfilVenta: c.perfilId ?? (f.perfilVenta?.perfil?.id || null),
      descuentoSellerPct: descPct,
      precioActual: f.precioPubActual ?? null,
      precioFinal: f.precioFinalCalc ?? null,
      deadline: f.finishDate || null,
      ...(Array.isArray(f._otrasCampanias) && f._otrasCampanias.length > 0
        ? { otrasCampanias: f._otrasCampanias }
        : {}),
    },
  }
}

/**
 * clasificarCentralPromos — clasificador puro (vista 🎯 y agente).
 *
 * @param {Object} datos — del loader (`cargarDatosParaClasificador`) o de la
 *   vista con las MISMAS colecciones:
 *   - promosCampanias    ← campanas tipo='promo' → campanaPromoALegacyMaster
 *   - promosPorItem      ← campanasItems (sin ads) → campanasItemsAPromosPorItem
 *   - publicacionesCentral ← publicaciones → pubCanonicaACentralPromos
 *   - productos, catalogoLinks
 *   - ventas30d          ← ordenes ventana 90d, shape cascada (perfil de venta)
 *   - eventosCalendario  ← catalogoEventosCalendario paisId AR
 *   - reglasPromo        ← Reglas de Decisión central-promociones (cascada)
 *   - config             ← doc empresa (._aprendizajes)
 */
export function clasificarCentralPromos({
  promosCampanias = [],
  promosPorItem = [],
  publicacionesCentral = [],
  productos = [],
  catalogoLinks = [],
  ventas30d = [],
  eventosCalendario = [],
  reglasPromo = null,
  config = {},
} = {}) {
  const reglas = reglasPromo || getDefaultsParametros('central-promociones') || {}

  const { filas, empresaPromedios, campaniasEvaluadas } = construirFilasCentralPromos({
    campaniasMaster: promosCampanias,
    itemsPorCampania: [],
    promosPorItem,
    items: publicacionesCentral,
    productos,
    catalogoLinks,
    ventas: ventas30d,
    eventosCalendario,
    reglas,
    aprendizajes: config?._aprendizajes || null,
    conexionFiltro: null,
  })

  const secciones = {
    sumarse_promo: [],
    promo_propia: [],
    sumarse_apagando_publi: [],
    apagar_publi_promo: [],
    bajarse_promo: [],
  }
  const declarados = { mantener: 0, noSumarse: 0, sinStock: 0, sinDatos: 0 }
  let pausadas = 0

  // 1) Filtrar pausadas (ML deja precios viejos/bloqueadores — datos no
  //    accionables) y contar declarados.
  const accionables = []
  for (const f of filas) {
    if (f.statusPub && f.statusPub !== 'active') { pausadas++; continue }
    const id = f.accion?.id
    if (id === 'mantener') { declarados.mantener++; continue }
    if (id === 'no-sumarse') { declarados.noSumarse++; continue }
    if (id === 'sin-stock') { declarados.sinStock++; continue }
    if (id === 'sin-datos') { declarados.sinDatos++; continue }
    if (ACCION_A_SECCION[id]) accionables.push(f)
  }

  // 2) Consolidar por ítem DENTRO de cada sección (un ítem puede ser
  //    candidato en varias campañas → una sola tarea, la campaña más
  //    conveniente primero; las otras quedan listadas en metricas).
  const porSeccion = new Map()
  for (const f of accionables) {
    const sec = ACCION_A_SECCION[f.accion.id]
    if (!porSeccion.has(sec)) porSeccion.set(sec, new Map())
    const porItem = porSeccion.get(sec)
    const key = String(f.itemId)
    const prev = porItem.get(key)
    if (!prev) { porItem.set(key, f); continue }
    // criterio de "mejor fila": para acciones positivas gana el margen neto
    // más alto; para defensivas (apagar/bajarse) gana el más bajo (urgente).
    const defensiva = sec === 'apagar_publi_promo' || sec === 'bajarse_promo'
    const mPrev = prev.accion?._calc?.margenPostNeto ?? (defensiva ? Infinity : -Infinity)
    const mNew = f.accion?._calc?.margenPostNeto ?? (defensiva ? Infinity : -Infinity)
    const gana = defensiva ? (mNew < mPrev) : (mNew > mPrev)
    const ganador = gana ? f : prev
    const perdedor = gana ? prev : f
    ganador._otrasCampanias = [...(prev._otrasCampanias || []), perdedor.campaniaNombre].filter(Boolean)
    porItem.set(key, ganador)
  }

  // 3) La acción doble NO se duplica: si el ítem ya tiene tarea de apagar
  //    publi por estar EN una promo (apagar_publi_promo), la mitad 1 de
  //    "sumarse apagando publi" es el MISMO movimiento físico → una sola.
  let publiCubierta = 0
  const enApagar = porSeccion.get('apagar_publi_promo') || new Map()
  const enSumarseApagando = porSeccion.get('sumarse_apagando_publi') || new Map()
  for (const key of [...enSumarseApagando.keys()]) {
    if (enApagar.has(key)) {
      enSumarseApagando.delete(key)
      publiCubierta++
    }
  }

  // 4) Ordenar y mapear al shape de tarea.
  const ordenar = (arr, sec) => {
    const defensiva = sec === 'apagar_publi_promo' || sec === 'bajarse_promo'
    return arr.sort((a, b) => {
      const ma = a.accion?._calc?.margenPostNeto ?? 0
      const mb = b.accion?._calc?.margenPostNeto ?? 0
      return defensiva ? (ma - mb) : (mb - ma)
    })
  }
  for (const sec of SECCIONES_CENTRAL_PROMOS) {
    const porItem = porSeccion.get(sec)
    if (!porItem) continue
    secciones[sec] = ordenar([...porItem.values()], sec).map(f => itemDesdeFila(f, sec))
  }

  return {
    secciones,
    excluidos: {
      ...declarados,
      pausadas,
      publiCubierta,       // mitad 1 ya cubierta por apagar_publi_promo
      totalFilas: filas.length,
      campaniasEvaluadas,
      umbralOkPct: (() => {
        const pisoAbs = reglas?.margenNetoPisoAbsoluto ?? 0.10
        const pisoRel = reglas?.margenNetoPisoRelativo ?? 0.50
        const neto = empresaPromedios?.netoPct ?? null
        return neto != null ? Math.max(pisoAbs, pisoRel * neto) : pisoAbs
      })(),
      fuentePromedios: empresaPromedios?.fuente || null,
    },
    // trazas para la vista (chips de conteo del motor, tabla)
    _filas: filas,
  }
}

// helpers exportados para la vista
export const fmtPctCentralPromos = pct
export const fmtMoneyCentralPromos = money
